import React, { useState } from 'react';
import { Card, CardBody, Typography, IconButton } from "@material-tailwind/react";
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

/**
 * DriverTypeSummaryPivot - Displays a pivot table showing driver transactions by sub_type
 * @param {Object} props
 * @param {Array} props.driverPivot.rows - Array of driver objects with transaction data
 * @param {Array} props.driverPivot.columns - Array of sub_type names
 * @param {Object} props.driverPivot.totals - Column totals by sub_type
 * @param {number} props.balance - Overall balance (total debit - total credit)
 */
const DriverTypeSummaryPivot = ({ driverPivot, balance }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    if (!driverPivot?.rows?.length) {
        return null;
    }

    // Pagination calculations
    const totalPages = Math.ceil(driverPivot.rows.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentRows = driverPivot.rows.slice(startIndex, endIndex);

    const handlePreviousPage = () => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    };

    return (
        <Card className="bg-white rounded-2xl shadow-xl p-6">
            <CardBody>
                <div className="flex justify-between items-center mb-4">
                    <Typography variant="h5" color="blue-gray">
                        Summary by Driver & Sub Type (Net Amount)
                    </Typography>
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-blue-gray-700">Per page:</label>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            className="px-3 py-1.5 border border-blue-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-max table-auto text-left border-collapse">
                        <thead>
                            <tr className="border-b-2 border-blue-gray-200">
                                <th className="p-3 bg-blue-gray-50 font-bold text-sm sticky left-0 z-10">Driver Name</th>
                                {driverPivot.columns.map(col => (
                                    <th key={col} className="p-3 bg-blue-gray-50 font-bold text-sm text-right whitespace-nowrap">
                                        {col}
                                    </th>
                                ))}
                                <th className="p-3 bg-blue-gray-50 font-bold text-sm text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentRows.map(row => (
                                <tr key={row.id} className="border-b border-blue-gray-100 hover:bg-blue-gray-50 transition-colors">
                                    <td className="p-3 text-sm font-medium sticky left-0 bg-white z-10">
                                        <Link to={`/drivers/${row.id}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                                            {row.name}
                                        </Link>
                                    </td>
                                    {driverPivot.columns.map(col => {
                                        const val = row.types[col] || 0;
                                        return (
                                            <td key={col} className={`p-3 text-sm text-right font-semibold whitespace-nowrap ${val > 0 ? 'text-red-600' : val < 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                                {val !== 0 ? val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                            </td>
                                        );
                                    })}
                                    <td className={`p-3 text-sm text-right font-bold ${row.total > 0 ? 'text-red-700' : row.total < 0 ? 'text-green-700' : 'text-gray-600'}`}>
                                        {row.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                </tr>
                            ))}
                            {/* Totals Row */}
                            <tr className="bg-blue-gray-50 font-bold border-t-2 border-blue-gray-300">
                                <td className="p-3 text-sm text-right sticky left-0 bg-blue-gray-50 z-10">TOTALS:</td>
                                {driverPivot.columns.map(col => {
                                    const val = driverPivot.totals[col] || 0;
                                    return (
                                        <td key={col} className={`p-3 text-sm text-right whitespace-nowrap ${val > 0 ? 'text-red-700' : val < 0 ? 'text-green-700' : 'text-gray-600'}`}>
                                            {val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                    );
                                })}
                                <td className={`p-3 text-sm text-right ${balance > 0 ? 'text-red-700' : balance < 0 ? 'text-green-700' : 'text-gray-600'}`}>
                                    {balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-blue-gray-200">
                    <Typography variant="small" color="blue-gray" className="font-normal">
                        Showing <strong>{startIndex + 1}</strong> to <strong>{Math.min(endIndex, driverPivot.rows.length)}</strong> of <strong>{driverPivot.rows.length}</strong> drivers
                    </Typography>
                    <div className="flex gap-2">
                        <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            className="rounded-lg"
                        >
                            <FaChevronLeft className="h-4 w-4" />
                        </IconButton>
                        <Typography variant="small" color="blue-gray" className="font-normal px-3 py-2">
                            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                        </Typography>
                        <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className="rounded-lg"
                        >
                            <FaChevronRight className="h-4 w-4" />
                        </IconButton>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default DriverTypeSummaryPivot;
